import React from 'react'
import {
  NameScope,
  renderPattern,
  validatePattern,
  tokensFor,
  SAMPLE_VALUES,
  NameValues,
} from '../utils/naming'

/**
 * One naming-pattern input: text field + scoped token picker + live preview + problems.
 *
 * Three design points that are not cosmetic:
 *
 *  - The token picker offers ONLY the tokens valid for this scope (016-template-model §6.6).
 *    {Version} is absent from group patterns because a group outlives any single release and
 *    would need renaming AND reassigning on every update; {Intent} is absent from app patterns
 *    because one app object carries several intents. Making the invalid choice unreachable
 *    beats validating it after the fact.
 *  - Problems BLOCK. Generated names are read-only at packaging time, so an admin who saves a
 *    bad pattern cannot correct the resulting object name later — the field surfaces the
 *    reason inline rather than letting it through to a publish-time failure.
 *  - A chip inserts AT THE CARET, separated from its neighbours, and replaces the selection
 *    (see insertTokenAt). The picker exists so a name can be composed without typing braces;
 *    appending regardless of the caret left typing them by hand as the only way to put a token
 *    in the middle, in a string that becomes a permanent Intune object name.
 *
 * The preview is rendered locally for responsiveness. It is deliberately the same logic the
 * backend uses (see the parity test in tenant-backend/tests/test_naming_parity.py); the
 * authoritative answer is POST /intune-templates/preview-names.
 */
export interface TokenInsertion {
  value: string
  /** Where the caret belongs afterwards: immediately after the inserted token. */
  caret: number
}

/** Characters that already read as a separator, so an insert beside one does not add a second.
 *  Ordered: an even split between candidates resolves to the first, which is the `-` every
 *  shipped default uses. */
const SEPARATORS = ['-', '_', '.', ' ']

const isSeparator = (ch: string) => SEPARATORS.includes(ch)

interface Span {
  start: number
  end: number
}

/** Every match of `re` in the text, as half-open [start, end) offsets. */
function spansOf(text: string, re: RegExp): Span[] {
  const spans: Span[] = []
  for (let m = re.exec(text); m; m = re.exec(text)) {
    spans.push({ start: m.index, end: m.index + m[0].length })
  }
  return spans
}

/** Every `{Token}` in the text. */
const tokenSpans = (text: string): Span[] => spansOf(text, /\{[^{}]*\}/g)

/** Every `[Marker]` in the text. `[Update Only]` is what tells the update-only app object from
 *  the app it patches in a sorted Intune list, and the same shape is how a tenant writes any
 *  other standing marker. */
const markerSpans = (text: string): Span[] => spansOf(text, /\[[^[\]]*\]/g)

/**
 * The marker an insert would land INSIDE, if any.
 *
 * A caret at offset 0 counts as inside a LEADING marker: there is nothing before it to insert
 * into, and a token there pushes the marker out of the position the name is read and sorted by.
 * A selection reaching past the closing bracket is NOT inside — it was deliberately dragged
 * across the marker, and is replaced like any other selection.
 */
const markerAround = (markers: Span[], start: number, end: number): Span | undefined =>
  markers.find((m) => end < m.end && (start > m.start || (m.start === 0 && start === 0)))

/** The separator the pattern already uses, counted over the text that JOINS its parts — so a chip
 *  dropped into `{AppName}_{Arch}` does not mix a dash into an underscore-style name. Tokens and
 *  markers are both removed first: the space in `[Do Not Deploy]-{AppName}` is that marker's
 *  wording, and counting it would join the next chip on with a space. */
function inferSeparator(text: string): string {
  const literal = text.replace(/\{[^{}]*\}/g, '').replace(/\[[^[\]]*\]/g, '')
  let best = SEPARATORS[0]
  let seen = 0
  for (const sep of SEPARATORS) {
    const n = literal.split(sep).length - 1
    if (n > seen) {
      best = sep
      seen = n
    }
  }
  return best
}

/**
 * Where a clicked token lands in the pattern, and what has to go around it.
 *
 * `selection` is the input's current selection, or null when the field has never held the caret.
 * Null appends, because an untouched input reports offset 0 and would silently PREPEND. A
 * non-empty selection is REPLACED, which is what makes swapping one token for another
 * select-and-click.
 *
 * Two rules stop a click producing a name nobody would type:
 *
 *  - A token is ATOMIC. A caret parked inside `{AppName}` means the token next to it, so the
 *    insert moves to the nearer brace rather than yielding `{App{Version}Name}` — which is not
 *    a token at all, so it would render literally into a permanent Intune object name. A
 *    selection that only clips a token swallows it whole, for the same reason.
 *  - A `[Marker]` is a NO-GO ZONE. Not merely atomic: an insert anywhere inside it lands AFTER
 *    the closing bracket, never before and never split across it. `[Update Only]` is the
 *    marker's whole job, and `[Update-{Version}- Only]` names nothing an admin can scan for.
 *  - Neighbouring values are SEPARATED. `{AppName}{Version}` renders as `7-Zip26.02`, so a
 *    separator is added on whichever side does not already have one.
 *
 * Pure, so the caret arithmetic is asserted without a DOM.
 */
export function insertTokenAt(
  value: string,
  token: string,
  selection: { start: number; end: number } | null,
): TokenInsertion {
  const text = value ?? ''
  const clamp = (n: number) => Math.max(0, Math.min(n, text.length))
  let start = selection ? clamp(Math.min(selection.start, selection.end)) : text.length
  let end = selection ? clamp(Math.max(selection.start, selection.end)) : text.length

  const spans = tokenSpans(text)
  const markers = markerSpans(text)
  const marker = markerAround(markers, start, end)
  if (marker) {
    start = end = marker.end
  } else if (start === end) {
    const inside = spans.find((s) => s.start < start && start < s.end)
    if (inside) start = end = start - inside.start < inside.end - start ? inside.start : inside.end
  } else {
    // Markers as well as tokens: a selection dragged across one takes it whole, so no bracket is
    // left unclosed by the replacement.
    for (const s of [...spans, ...markers]) {
      if (s.start < start && start < s.end) start = s.start
      if (s.start < end && end < s.end) end = s.end
    }
  }

  const separator = inferSeparator(text)
  const before = text.slice(0, start)
  const after = text.slice(end)
  const lead = before && !isSeparator(before[before.length - 1]) ? separator : ''
  const trail = after && !isSeparator(after[0]) ? separator : ''
  return {
    value: before + lead + token + trail + after,
    caret: start + lead.length + token.length,
  }
}

export interface NamingPatternFieldProps {
  label: string
  /** Which token set is legal here. */
  scope: NameScope
  value: string
  onChange: (next: string) => void
  /** Values to preview against; falls back to the documented samples. */
  previewValues?: NameValues
  /** Explains what object this pattern names. */
  hint?: string
  disabled?: boolean
  /** Called with true whenever the pattern has blocking problems, so a wizard step can
   *  refuse to advance rather than discovering it at publish. */
  onValidityChange?: (hasProblems: boolean) => void
}

const NamingPatternField: React.FC<NamingPatternFieldProps> = ({
  label,
  scope,
  value,
  onChange,
  previewValues,
  hint,
  disabled,
  onValidityChange,
}) => {
  const values = previewValues ?? SAMPLE_VALUES
  const { problems, warnings } = validatePattern(value, scope)
  const preview = renderPattern(value, values)
  /** A pattern with {DeploymentName} renders differently for a package's later deployments. */
  const laterPreview = /\{deploymentname\}/i.test(value)
    ? renderPattern(value, { ...values, deploymentname: 'AVD' })
    : null
  const tokens = tokensFor(scope)

  // Report validity upward on every change rather than only on blur: a wizard's Next button
  // should be disabled the moment the pattern breaks, not after the field loses focus.
  React.useEffect(() => {
    onValidityChange?.(problems.length > 0)
  }, [problems.length, onValidityChange])

  const inputRef = React.useRef<HTMLInputElement | null>(null)
  /** Whether the field has ever held the caret. An input that never has reports offset 0, which
   *  is indistinguishable from a caret deliberately parked at the start. */
  const hasHeldCaret = React.useRef(false)
  /** An insert waiting for its caret, once React has re-rendered with the new value. */
  const pending = React.useRef<TokenInsertion | null>(null)

  // The caret cannot be moved before the text it indexes into exists, and `value` is the parent's
  // to accept — so the caret is placed after the render that brings the new value back.
  React.useLayoutEffect(() => {
    const el = inputRef.current
    const next = pending.current
    if (!el || !next || el.value !== next.value) return
    pending.current = null
    el.focus()
    el.setSelectionRange(next.caret, next.caret)
  })

  const insertToken = (token: string) => {
    if (disabled) return
    // Read live rather than from a remembered copy: the chip does not take focus, so the input's
    // own selection is authoritative however the caret got there — arrow keys, a click, a drag.
    const el = inputRef.current
    const selection =
      el && hasHeldCaret.current
        ? { start: el.selectionStart ?? el.value.length, end: el.selectionEnd ?? el.value.length }
        : null
    const next = insertTokenAt(value, token, selection)
    pending.current = next
    onChange(next.value)
  }

  return (
    <div className="mb-5">
      <label className="label">{label}</label>
      {hint && <p className="text-xs text-theme-text-tertiary mb-2">{hint}</p>}

      <input
        ref={inputRef}
        type="text"
        value={value}
        disabled={disabled}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          hasHeldCaret.current = true
        }}
        className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60 font-mono text-sm"
        aria-invalid={problems.length > 0}
      />

      {/* Only tokens legal in this scope are offered — the restricted ones are not rendered
          at all, so an invalid pattern cannot be built by clicking. */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {tokens.map((t) => (
          <button
            key={t.token}
            type="button"
            disabled={disabled}
            // Keeps the caret in the field: a chip that took focus would drop the selection it
            // is about to replace.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertToken(t.token)}
            title={t.hint ? `${t.label} — ${t.hint}` : `${t.label} — e.g. ${t.sample}`}
            className="px-2 py-0.5 text-xs font-mono rounded border border-theme-border-primary text-theme-text-secondary hover:text-theme-text-primary disabled:opacity-50"
          >
            {t.token}
          </button>
        ))}
      </div>

      {laterPreview === null ? (
        <p className="mt-2 text-xs text-theme-text-tertiary">
          Preview <span className="font-mono text-theme-text-primary">{preview || '—'}</span>
        </p>
      ) : (
        <>
          <p className="mt-2 text-xs text-theme-text-tertiary">
            Preview, first deployment of a package{' '}
            <span className="font-mono text-theme-text-primary">{preview || '—'}</span>
          </p>
          <p className="mt-1 text-xs text-theme-text-tertiary">
            Another deployment, e.g. one named AVD{' '}
            <span className="font-mono text-theme-text-primary">{laterPreview}</span>
          </p>
          <p className="mt-1 text-xs text-theme-text-tertiary">
            {'{DeploymentName}'} is empty for a package's first deployment and holds the deployment
            name only when a package has more than one deployment.
          </p>
        </>
      )}
      {/* Says the pattern is not a closed vocabulary: whatever is not a token survives verbatim,
          which is how a tenant gets its own prefix into every generated name. */}
      <p className="mt-1 text-xs text-theme-text-tertiary">
        Anything outside the tokens is kept literally — type a prefix, separators or wording of
        your own, e.g. <span className="font-mono">CF-</span>.
      </p>

      {problems.map((p, i) => (
        <p key={`p${i}`} className="mt-1 text-xs text-red-400">
          {p.token ? `${p.token}: ` : ''}
          {p.reason}
        </p>
      ))}
      {warnings.map((w, i) => (
        <p key={`w${i}`} className="mt-1 text-xs text-amber-400">
          {w}
        </p>
      ))}
    </div>
  )
}

export default NamingPatternField
